import { useCallback, useEffect, useRef, useState } from 'react'
import { EDIT_WITH_AI_POST_GENERATION_ID } from '@/constants/editWithAiDom'

export type EditWithAiSubmitSource = 'submit' | 'suggestion' | 'tryAgain'

export interface UseEditWithAiPromptOptions {
  /** Runs the generation. Resolve `true` to enter the post-generation state. */
  onSubmit: (prompt: string, source: EditWithAiSubmitSource) => Promise<boolean>
  /** Aborts an in-flight generation. */
  onCancel?: () => void
  /** True while a generation is in flight. Redux-derived in Editor/AI Studio,
   *  local state in Batch Studio. Never owned by this hook. */
  isProcessing: boolean

  /** Controlled submitted-prompt (AI Studio holds it in the aiFashion slice).
   *  Omit both to run uncontrolled with local state (Editor, Batch). */
  submittedPrompt?: string
  onSubmittedPromptChange?: (prompt: string | null) => void

  /** Editor-only today; default false so migrating a new consumer is a no-op. */
  closePostGenerationOnOutsideClick?: boolean
  /** Restores the pre-submit prompt when the user cancels, but only if the field
   *  is still empty — so it doesn't clobber text typed during generation. */
  restorePromptOnCancel?: boolean
  /** Fires when the post-generation group is dismissed via close (X button or
   *  outside click) — not on editPrompt. Editor uses this to reset handler-level
   *  session state; other consumers can omit it. */
  onClose?: () => void
}

export interface UseEditWithAiPromptResult {
  prompt: string
  setPrompt: (value: string) => void
  submittedPrompt: string
  showPostGeneration: boolean
  submit: (input?: string, source?: 'submit' | 'suggestion') => Promise<void>
  tryAgain: () => Promise<void>
  cancel: () => void
  editPrompt: () => void
  closePostGeneration: () => void
  /** Hides the post-generation group without firing `onClose` — for transitions
   *  that aren't a dismissal (e.g. the user starts typing a new prompt). */
  hidePostGeneration: () => void
  /** Marks the post-generation state as shown without changing the submitted
   *  prompt. For flows that generate outside of `submit`/`tryAgain` (e.g. the
   *  textbox's own quick-action popover) but still need to reveal the group. */
  markSuccess: () => void
}

export function useEditWithAiPrompt({
  onSubmit,
  onCancel,
  isProcessing,
  submittedPrompt: submittedPromptProp,
  onSubmittedPromptChange,
  closePostGenerationOnOutsideClick = false,
  restorePromptOnCancel = false,
  onClose,
}: UseEditWithAiPromptOptions): UseEditWithAiPromptResult {
  const [prompt, setPrompt] = useState('')
  const [localSubmittedPrompt, setLocalSubmittedPrompt] = useState('')
  const [localShowPostGeneration, setLocalShowPostGeneration] = useState(false)
  const preSubmitPromptRef = useRef('')

  const isControlled = submittedPromptProp !== undefined
  const submittedPrompt = isControlled ? submittedPromptProp : localSubmittedPrompt
  const showPostGeneration = isControlled ? Boolean(submittedPromptProp) : localShowPostGeneration

  const markSuccess = useCallback(
    (value?: string) => {
      if (isControlled) {
        onSubmittedPromptChange?.(value ?? submittedPromptProp ?? '')
      } else {
        if (value !== undefined) setLocalSubmittedPrompt(value)
        setLocalShowPostGeneration(true)
      }
    },
    [isControlled, onSubmittedPromptChange, submittedPromptProp],
  )

  // Hides the group without firing onClose — used by editPrompt, which is a
  // continuation (re-edit and resubmit), not a dismissal.
  const hidePostGeneration = useCallback(() => {
    if (isControlled) onSubmittedPromptChange?.(null)
    else setLocalShowPostGeneration(false)
  }, [isControlled, onSubmittedPromptChange])

  const closePostGeneration = useCallback(() => {
    hidePostGeneration()
    onClose?.()
  }, [hidePostGeneration, onClose])

  const submit = useCallback(
    async (input?: string, source: 'submit' | 'suggestion' = 'submit') => {
      if (isProcessing) return
      const value = input ?? prompt
      preSubmitPromptRef.current = value
      setPrompt('')
      const success = await onSubmit(value, source)
      if (success) markSuccess(value)
    },
    [isProcessing, prompt, onSubmit, markSuccess],
  )

  const tryAgain = useCallback(async () => {
    if (isProcessing || !submittedPrompt) return
    const success = await onSubmit(submittedPrompt, 'tryAgain')
    if (success) markSuccess(submittedPrompt)
  }, [isProcessing, onSubmit, submittedPrompt, markSuccess])

  const cancel = useCallback(() => {
    onCancel?.()
    // Capture and clear the ref before scheduling the update — a functional
    // setState updater runs at flush time, not call time, so reading the ref
    // there would see whatever this line leaves it as, not what was here now.
    const preSubmitPrompt = preSubmitPromptRef.current
    preSubmitPromptRef.current = ''
    if (restorePromptOnCancel) {
      setPrompt((current) => (current === '' ? preSubmitPrompt : current))
    }
  }, [onCancel, restorePromptOnCancel])

  const editPrompt = useCallback(() => {
    setPrompt(submittedPrompt)
    hidePostGeneration()
  }, [submittedPrompt, hidePostGeneration])

  useEffect(() => {
    if (!closePostGenerationOnOutsideClick || !showPostGeneration) return
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      const container = document.getElementById(EDIT_WITH_AI_POST_GENERATION_ID)
      if (container && target && container.contains(target)) return
      closePostGeneration()
    }
    window.addEventListener('mousedown', onMouseDown, { passive: true })
    return () => window.removeEventListener('mousedown', onMouseDown)
  }, [closePostGenerationOnOutsideClick, showPostGeneration, closePostGeneration])

  return {
    prompt,
    setPrompt,
    submittedPrompt,
    showPostGeneration,
    submit,
    tryAgain,
    cancel,
    editPrompt,
    closePostGeneration,
    hidePostGeneration,
    markSuccess: () => markSuccess(),
  }
}
